/**
 * 智能助手（Agent）数据层（P-D④）。
 *
 * 会话/消息纯本地持久化（localStorage，注入可测）；`mockAgentReply` 是接真 LLM 前的占位编排：
 * 回一段"我打算怎么做"的计划文本，证明交互与数据流。接真时把 provider 换成后端 /api/v2/agent 即可，
 * 类型与 UI 不变。分层纪律同 canvasLibrary：纯逻辑、storage 注入、window 只在缺省解析时触达。
 */

export interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

export interface AgentSession {
  id: string;
  title: string;
  /** 当前模式：创作 / 图片 / 视频 / 音乐 */
  mode: string;
  messages: AgentMessage[];
  updatedAt: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const INDEX_KEY = 'videoai-pro.agent.index.v1';
const ITEM_PREFIX = 'videoai-pro.agent.session.';

function itemKey(id: string): string {
  return ITEM_PREFIX + id;
}

function resolve(storage: StorageLike | undefined): StorageLike | null {
  if (storage !== undefined) {
    return storage;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readIndex(s: StorageLike): AgentSession[] {
  const raw = s.getItem(INDEX_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AgentSession[]) : [];
  } catch {
    return [];
  }
}

function writeIndex(s: StorageLike, list: AgentSession[]): void {
  try {
    s.setItem(INDEX_KEY, JSON.stringify(list));
  } catch {
    /* 配额异常不阻断 */
  }
}

export function newSessionId(now: number = Date.now()): string {
  const rand = Math.random().toString(36).slice(2, 7);
  return `agent-${now.toString(36)}-${rand}`;
}

/** 会话摘要（列表用，不含全部消息）。 */
export function listSessions(storage?: StorageLike): AgentSession[] {
  const s = resolve(storage);
  if (s === null) {
    return [];
  }
  return readIndex(s)
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function loadSession(id: string, storage?: StorageLike): AgentSession | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  const raw = s.getItem(itemKey(id));
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as AgentSession;
    return Array.isArray(parsed.messages) ? parsed : null;
  } catch {
    return null;
  }
}

/** 保存会话：写全文 + upsert 索引（索引存摘要，标题取首条用户消息）。 */
export function saveSession(session: AgentSession, storage?: StorageLike): AgentSession | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  const nowIso = new Date().toISOString();
  const withMeta: AgentSession = { ...session, updatedAt: nowIso };
  try {
    s.setItem(itemKey(withMeta.id), JSON.stringify(withMeta));
  } catch {
    return null;
  }
  const rest = readIndex(s).filter((m) => m.id !== withMeta.id);
  rest.push(withMeta);
  writeIndex(s, rest);
  return withMeta;
}

export function deleteSession(id: string, storage?: StorageLike): boolean {
  const s = resolve(storage);
  if (s === null) {
    return false;
  }
  const existed = s.getItem(itemKey(id)) !== null;
  s.removeItem(itemKey(id));
  writeIndex(s, readIndex(s).filter((m) => m.id !== id));
  return existed;
}

/** 会话标题：首条用户消息前 20 字，否则"新对话"。 */
export function sessionTitle(session: AgentSession): string {
  const firstUser = session.messages.find((m) => m.role === 'user');
  const t = firstUser?.content.trim() ?? '';
  return t === '' ? session.title || '新对话' : t.slice(0, 20);
}

/**
 * mock 编排回复（接真 LLM 前占位）：回一段"打算怎么做"的计划，呼应用户意图。
 * 纯函数可测。接真时替换为后端 agent 规划结果。
 */
export function mockAgentReply(input: string, mode: string): string {
  const idea = input.trim();
  return [
    `收到你的想法（${mode}）：「${idea}」。`,
    '',
    '我打算这样帮你编排整条流程：',
    '1. 新建画布并落下「文学剧本」节点，按你的想法写出剧本梗概与高密度对白；',
    '2. 一键「拆分成导演剧本」→ 生成资产卡（角色/场景/道具）→ 展开分镜；',
    '3. 逐镜生成关键帧 → 配音（按角色音色）→ 分镜视频；',
    '4. 需要摆位/机位的镜头，送进 3D 导演台打磨后回写。',
    '',
    '（当前为占位编排，接入真实 LLM 后将自动规划并可直接落画布。）',
  ].join('\n');
}
